import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Grid,
  List,

  Search,
  Heart,
  X,


  ShoppingCart,
  Eye,




} from 'lucide-react';
import { useGalleryConfig } from '../context/GalleryConfigContext';
import { useGalleryTheme } from '../context/GalleryThemeContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useGalleryCart } from '../context/GalleryCartContext';
import { GalleryEvent, GalleryPhoto, PhotoFilters } from '../types/gallery';
import { galleryApiService } from '../services/galleryApiService';
import Button from '../components/common/Button';
import LoadingScreen from '../components/common/LoadingScreen';
import ErrorScreen from '../components/common/ErrorScreen';

const PhotoGalleryPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { config } = useGalleryConfig();
  const { theme } = useGalleryTheme();
  const { customer, isAuthenticated } = useCustomerAuth();
  const { addItem } = useGalleryCart();

  const [event, setEvent] = useState<GalleryEvent | null>(null);
  const [photos, setPhotos] = useState<GalleryPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [password, setPassword] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState<GalleryPhoto | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filters] = useState<PhotoFilters>({
    sortBy: 'created',
    sortOrder: 'desc',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // Load event and initial photos
  useEffect(() => {
    const loadEvent = async () => {
      if (!config?.photographerId || !eventId) return;

      try {
        setLoading(true);
        setError(null);

        const eventData = await galleryApiService.getEvent(
          config.photographerId,
          eventId,
          password || undefined
        );

        setEvent(eventData);

        // Track event view
        await galleryApiService.trackEvent(config.photographerId, {
          type: 'page_view',
          eventId: eventId,
          metadata: { page: 'gallery' },
        });

        // Load photos
        await loadPhotos(eventData.id, true);

        setPasswordRequired(false);
      } catch (err: any) {
        if (err.response?.status === 403) {
          setPasswordRequired(true);
          setError('This gallery is password protected');
        } else {
          setError('Failed to load gallery');
        }
      } finally {
        setLoading(false);
      }
    };

    loadEvent();
  }, [config, eventId, password]);

  // Load photos with filters
  const loadPhotos = useCallback(
    async (currentEventId: string, reset = false) => {
      if (!config?.photographerId) return;

      try {
        setPhotosLoading(true);

        const currentPage = reset ? 1 : page;
        const photosResponse = await galleryApiService.getEventPhotos(
          config.photographerId,
          currentEventId,
          {
            ...filters,
            search: searchQuery || undefined,
            page: currentPage,
            limit: 24,
          }
        );

        if (reset) {
          setPhotos(photosResponse.items);
          setPage(1);
        } else {
          setPhotos(prev => [...prev, ...photosResponse.items]);
        }

        setHasMore(photosResponse.pagination.hasNext);
      } catch {
        setError('Failed to load photos');
      } finally {
        setPhotosLoading(false);
      }
    },
    [config, filters, searchQuery, page]
  );

  // Handle password submission
  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordRequired(false);
    setPassword(password);
  };

  // Handle photo selection
  const handlePhotoSelect = async (photo: GalleryPhoto) => {
    setSelectedPhoto(photo);

    // Track photo view
    if (config?.photographerId) {
      await galleryApiService.trackEvent(config.photographerId, {
        type: 'photo_view',
        eventId: eventId,
        photoId: photo.id,
      });
    }
  };

  // Handle add to cart
  const handleAddToCart = (photo: GalleryPhoto, resolution: 'web' | 'print' | 'full') => {
    addItem({
      photoId: photo.id,
      photo,
      resolution,
      quantity: 1,
      price: photo.pricing[resolution],
    });

    // Track cart add
    if (config?.photographerId) {
      galleryApiService.trackEvent(config.photographerId, {
        type: 'cart_add',
        eventId: eventId,
        photoId: photo.id,
        metadata: { resolution, price: photo.pricing[resolution] },
      });
    }
  };

  // Handle favorite toggle
  const handleFavoriteToggle = async (_photo: GalleryPhoto) => {
    if (!isAuthenticated || !customer || !config?.photographerId) {
      navigate('/login');
      return;
    }

    try {
      // This would toggle the favorite status
      // For now, just show feedback
    } catch {
      // Error handled via UI state
    }
  };

  // Handle search
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (event) {
      loadPhotos(event.id, true);
    }
  };

  // Load more photos
  const handleLoadMore = () => {
    if (hasMore && !photosLoading && event) {
      setPage(prev => prev + 1);
      loadPhotos(event.id, false);
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading gallery..." />;
  }

  if (passwordRequired) {
    return (
      <div className="max-w-md mx-auto mt-12">
        <div className="p-6 rounded-lg shadow-lg" style={{ backgroundColor: theme.colors.surface }}>
          <h2
            className="text-2xl font-bold mb-4 text-center"
            style={{ color: theme.colors.text.primary }}
          >
            Password Required
          </h2>
          <p className="text-center mb-6" style={{ color: theme.colors.text.secondary }}>
            This gallery is password protected. Please enter the password to view photos.
          </p>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
              style={{
                borderColor: theme.colors.border,
              }}
              required
            />
            <Button type="submit" fullWidth>
              Access Gallery
            </Button>
          </form>
        </div>
      </div>
    );
  }

  if (error && !event) {
    return (
      <ErrorScreen
        title="Gallery Not Found"
        message={error}
        showRetry
        onRetry={() => window.location.reload()}
      />
    );
  }

  if (!event) {
    return <div>Gallery not found</div>;
  }

  return (
    <div className="space-y-6">
      {/* Gallery Header */}
      <div className="text-center">
        <h1
          className="text-3xl md:text-4xl font-bold mb-4"
          style={{ color: theme.colors.text.primary }}
        >
          {event.title}
        </h1>
        {event.description && (
          <p className="text-lg mb-4" style={{ color: theme.colors.text.secondary }}>
            {event.description}
          </p>
        )}
        <div className="flex items-center justify-center space-x-6 text-sm">
          <span style={{ color: theme.colors.text.muted }}>
            {new Date(event.eventDate).toLocaleDateString()}
          </span>
          {event.location && (
            <span style={{ color: theme.colors.text.muted }}>{event.location}</span>
          )}
          <span style={{ color: theme.colors.text.muted }}>{photos.length} photos</span>
        </div>
      </div>

      {/* Toolbar */}
      <div
        className="flex flex-col md:flex-row items-center justify-between p-4 rounded-lg space-y-4 md:space-y-0"
        style={{ backgroundColor: theme.colors.surface }}
      >
        {/* Search */}
        <div className="relative w-full md:w-auto">
          <Search
            size={20}
            className="absolute left-3 top-1/2 transform -translate-y-1/2"
            style={{ color: theme.colors.text.muted }}
          />
          <input
            type="text"
            placeholder="Search photos..."
            value={searchQuery}
            onChange={e => handleSearch(e.target.value)}
            className="pl-10 pr-4 py-2 border rounded-lg w-full md:w-64 focus:outline-none focus:ring-2"
            style={{
              borderColor: theme.colors.border,
            }}
          />
        </div>

        {/* View Controls */}
        <div className="flex items-center space-x-2">
          <Button
            variant={viewMode === 'grid' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('grid')}
            leftIcon={<Grid size={16} />}
          >
            Grid
          </Button>
          <Button
            variant={viewMode === 'list' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
            leftIcon={<List size={16} />}
          >
            List
          </Button>
        </div>
      </div>

      {/* Photos Grid/List */}
      {viewMode === 'grid' ? (
        <PhotoGrid
          photos={photos}
          onPhotoSelect={handlePhotoSelect}
          onAddToCart={handleAddToCart}
          onFavoriteToggle={handleFavoriteToggle}
          showPrices={config?.settings?.showPrices ?? true}
        />
      ) : (
        <PhotoList
          photos={photos}
          onPhotoSelect={handlePhotoSelect}
          onAddToCart={handleAddToCart}
          onFavoriteToggle={handleFavoriteToggle}
          showPrices={config?.settings?.showPrices ?? true}
        />
      )}

      {/* Load More */}
      {hasMore && (
        <div className="text-center">
          <Button onClick={handleLoadMore} loading={photosLoading} variant="ghost">
            Load More Photos
          </Button>
        </div>
      )}

      {/* Photo Modal */}
      {selectedPhoto && (
        <PhotoModal
          photo={selectedPhoto}
          event={event}
          onClose={() => setSelectedPhoto(null)}
          onAddToCart={handleAddToCart}
          onFavoriteToggle={handleFavoriteToggle}
          showPrices={config?.settings?.showPrices ?? true}
        />
      )}
    </div>
  );
};

// Photo Grid Component
interface PhotoGridProps {
  photos: GalleryPhoto[];
  onPhotoSelect: (photo: GalleryPhoto) => void;
  onAddToCart: (photo: GalleryPhoto, resolution: 'web' | 'print' | 'full') => void;
  onFavoriteToggle: (photo: GalleryPhoto) => void;
  showPrices: boolean;
}

const PhotoGrid: React.FC<PhotoGridProps> = ({
  photos,
  onPhotoSelect,
  onAddToCart,
  onFavoriteToggle,
  showPrices,
}) => {

const { theme } = useGalleryTheme();
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
      {photos.map(photo => (
        <div
          key={photo.id}
          className="group relative aspect-square bg-gray-200 rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300"
          onClick={() => onPhotoSelect(photo)}
        >
          <img
            src={photo.urls.thumbnail}
            alt={photo.title || `Photo ${photo.id}`}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
          />

          {/* Overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex space-x-2">
              <button
                onClick={e => {
                  e.stopPropagation();
                  onPhotoSelect(photo);
                }}
                className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
              >
                <Eye size={16} />
              </button>
              <button
                onClick={e => {
                  e.stopPropagation();
                  onAddToCart(photo, 'web');
                }}
                className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
              >
                <ShoppingCart size={16} />
              </button>
              <button
                onClick={e => {
                  e.stopPropagation();
                  onFavoriteToggle(photo);
                }}
                className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
              >
                <Heart size={16} />
              </button>
            </div>
          </div>

          {/* Price Badge */}
          {showPrices && (
            <div
              className="absolute bottom-2 left-2 px-2 py-1 rounded text-xs font-medium text-white"
              style={{ backgroundColor: theme.colors.primary }}
            >
              ${photo.pricing.web}+
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Photo List Component (simplified for now)
const PhotoList: React.FC<PhotoGridProps> = props => {
  return <PhotoGrid {...props} />; // Can implement a different layout later
};

// Photo Modal Component
interface PhotoModalProps {
  photo: GalleryPhoto;
  event: GalleryEvent;
  onClose: () => void;
  onAddToCart: (photo: GalleryPhoto, resolution: 'web' | 'print' | 'full') => void;
  onFavoriteToggle: (photo: GalleryPhoto) => void;
  showPrices: boolean;
}

const PhotoModal: React.FC<PhotoModalProps> = ({
  photo,
  event: _event,
  onClose,
  onAddToCart,
  onFavoriteToggle,
  showPrices,
}) => {

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4">
      <div className="relative max-w-7xl max-h-full">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
        >
          <X size={20} />
        </button>

        {/* Photo */}
        <div className="relative">
          <img
            src={photo.urls.watermarked ?? photo.urls.web ?? photo.urls.thumbnail}
            alt={photo.title || `Photo ${photo.id}`}
            className="max-w-full max-h-[80vh] object-contain"
          />
        </div>

        {/* Photo Info Panel */}
        <div
          className="absolute bottom-0 left-0 right-0 p-6 text-white"
          style={{
            background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
          }}
        >
          <div className="flex items-end justify-between">
            <div>
              {photo.title && <h3 className="text-xl font-bold mb-2">{photo.title}</h3>}
              {photo.description && <p className="text-sm mb-4">{photo.description}</p>}
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-2">
              <button
                onClick={() => onFavoriteToggle(photo)}
                className="p-3 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-colors"
              >
                <Heart size={20} />
              </button>

              {showPrices && (
                <div className="flex space-x-2">
                  <Button
                    onClick={() => onAddToCart(photo, 'web')}
                    size="sm"
                    leftIcon={<ShoppingCart size={16} />}
                  >
                    Web ${photo.pricing.web}
                  </Button>
                  <Button
                    onClick={() => onAddToCart(photo, 'print')}
                    size="sm"
                    leftIcon={<ShoppingCart size={16} />}
                  >
                    Print ${photo.pricing.print}
                  </Button>
                  <Button
                    onClick={() => onAddToCart(photo, 'full')}
                    size="sm"
                    leftIcon={<ShoppingCart size={16} />}
                  >
                    Full ${photo.pricing.full}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoGalleryPage;
